import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { User, Mail, Coins, Loader, Zap, Calendar, CreditCard, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { fetchChatSettings, updateChatSettings } from '../services/chatService';
import './Profile.css';

function Profile() {
  const { user } = useAuth();
  const [userData, setUserData] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRenewUpdating, setAutoRenewUpdating] = useState(false);
  const [chatSettings, setChatSettings] = useState(null);
  const [chatModelSaving, setChatModelSaving] = useState(false);

  useEffect(() => {
    fetchUserData();
    fetchSubscriptionInfo();
    fetchChatSettings()
      .then(setChatSettings)
      .catch((error) => {
        console.error('Error fetching chat settings:', error);
      });
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users/me');
      setUserData(response.data);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await axios.get('/api/payments/my-subscription');
      if (response.data.has_subscription) {
        setSubscriptionInfo(response.data.subscription);
      } else {
        setSubscriptionInfo(null);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handleAutoRenewToggle = async (enabled) => {
    if (autoRenewUpdating) return;

    if (!enabled) {
      const confirmed = window.confirm(
        'Отключить автопродление? Текущая подписка останется активной до даты окончания.'
      );
      if (!confirmed) return;
    }

    try {
      setAutoRenewUpdating(true);
      const response = await axios.put(
        `/api/payments/subscription-settings?auto_renew=${enabled ? 'true' : 'false'}`
      );
      setSubscriptionInfo(prev => prev ? {
        ...prev,
        auto_renew: response.data.auto_renew,
      } : prev);
      toast.success(enabled ? 'Автопродление включено' : 'Автопродление отключено');
    } catch (error) {
      console.error('Error updating auto-renew:', error);
      toast.error('Не удалось изменить настройку автопродления');
    } finally {
      setAutoRenewUpdating(false);
    }
  };

  const handleChatModelChange = async (model) => {
    if (!chatSettings || chatModelSaving || model === chatSettings.model) return;
    try {
      setChatModelSaving(true);
      const updated = await updateChatSettings(model);
      setChatSettings(updated);
      toast.success('Модель чата сохранена');
    } catch (error) {
      console.error('Error updating chat model:', error);
      toast.error(error.message || 'Не удалось сохранить модель чата');
    } finally {
      setChatModelSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-loading">
          <Loader className="spinner" size={32} />
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>Профиль</h1>
        <p className="profile-subtitle">Информация о вашем аккаунте</p>
      </div>

      <div className="profile-content">
        <div className="profile-card">
          <div className="profile-info-item">
            <div className="profile-info-icon">
              <User size={20} />
            </div>
            <div className="profile-info-content">
              <div className="profile-info-label">Имя пользователя</div>
              <div className="profile-info-value">{userData?.username || user?.username || '—'}</div>
            </div>
          </div>

          <div className="profile-info-item">
            <div className="profile-info-icon">
              <Mail size={20} />
            </div>
            <div className="profile-info-content">
              <div className="profile-info-label">Email</div>
              <div className="profile-info-value">{userData?.email || user?.email || '—'}</div>
            </div>
          </div>

          <div className="profile-info-item">
            <div className="profile-info-icon">
              <Coins size={20} />
            </div>
            <div className="profile-info-content">
              <div className="profile-info-label">Баланс токенов</div>
              <div className="profile-info-value profile-balance">
                {userData?.bonus_balance !== undefined 
                  ? Math.round(userData.bonus_balance).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                  : user?.bonus_balance !== undefined
                  ? Math.round(user.bonus_balance).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                  : '0'}
              </div>
            </div>
          </div>
        </div>

        {subscriptionInfo && (
          <div className="profile-card profile-subscription-card">
            <div className="profile-subscription-header">
              <span className="profile-sub-label">Текущая подписка</span>
              <span className="profile-sub-badge">Активна</span>
            </div>
            <div className="profile-info-item">
              <div className="profile-info-icon">
                <Zap size={20} />
              </div>
              <div className="profile-info-content">
                <div className="profile-info-label">Тариф</div>
                <div className="profile-info-value profile-sub-name">{subscriptionInfo.tariff_name}</div>
              </div>
            </div>
            <div className="profile-info-item">
              <div className="profile-info-icon">
                <Calendar size={20} />
              </div>
              <div className="profile-info-content">
                <div className="profile-info-label">Действует до</div>
                <div className="profile-info-value">
                  {new Date(subscriptionInfo.expires_at).toLocaleDateString('ru-RU')}
                  <span className="profile-sub-days"> ({subscriptionInfo.days_remaining} дн.)</span>
                </div>
              </div>
            </div>
            <div className="profile-info-item">
              <div className="profile-info-icon">
                <CreditCard size={20} />
              </div>
              <div className="profile-info-content">
                <div className="profile-info-label">Автопродление</div>
                <label className="profile-auto-renew-toggle">
                  <input
                    type="checkbox"
                    checked={!!subscriptionInfo.auto_renew}
                    onChange={(e) => handleAutoRenewToggle(e.target.checked)}
                    disabled={autoRenewUpdating}
                  />
                  <span className={subscriptionInfo.auto_renew ? 'profile-auto-renew-on' : 'profile-auto-renew-off'}>
                    {autoRenewUpdating
                      ? 'Сохраняем...'
                      : subscriptionInfo.auto_renew
                        ? 'Включено'
                        : 'Отключено'}
                  </span>
                </label>
                <div className="profile-auto-renew-note">
                  При отключении текущий оплаченный период сохраняется до даты окончания.
                </div>
              </div>
            </div>
          </div>
        )}

        {chatSettings?.show_model_select && (
          <div className="profile-card">
            <div className="profile-info-item">
              <div className="profile-info-icon">
                <MessageSquare size={20} />
              </div>
              <div className="profile-info-content">
                <div className="profile-info-label">Модель чата</div>
                <select
                  className="profile-chat-model-select"
                  value={chatSettings.model}
                  onChange={(e) => handleChatModelChange(e.target.value)}
                  disabled={chatModelSaving}
                >
                  {(chatSettings.models || []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                <div className="profile-auto-renew-note">
                  Ответы ассистента будут генерироваться выбранной моделью.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;


